import React, { useEffect, useState } from 'react'

import { TagIcon } from '@toasttab/buffet-pui-icons'
import { LocationSection } from 'src/public/components/default_template/online_ordering/checkout/side_by_side/fulfillment/PickupSection'

import { useIsIntlRestaurant } from 'src/lib/js/hooks/useIsIntlRestaurant'
import { FulfillmentSectionV2 } from 'src/public/components/default_template/online_ordering/checkout/side_by_side/fulfillment/FulfillmentSection'
import { ScreenWidth, useIsMobile } from 'src/shared/js/utils/WindowContext'

import { useGlobalNav } from 'public/components/default_template/GlobalNavContext'
import { ToastCheckoutButton } from 'public/components/default_template/online_ordering/checkout/CheckoutModeButtons'
import CheckoutNote, {
  UberCheckoutNote
} from 'public/components/default_template/online_ordering/checkout/CheckoutNote'
import CustomerInfoSection from 'public/components/default_template/online_ordering/checkout/CustomerInfoSection'
import SubmitOrderButton from 'public/components/default_template/online_ordering/checkout/SubmitOrderButton'
import ContinuePaymentButton from 'public/components/default_template/online_ordering/checkout/international/ContinuePaymentButton'
import InternationalPaymentContainer from 'public/components/default_template/online_ordering/checkout/international/InternationalPaymentContainer'
import DiscountsSection from 'public/components/default_template/online_ordering/checkout/payment/Discounts/DiscountsSection'
import { useCart } from 'public/components/online_ordering/CartContext'
import { useCheckout } from 'public/components/online_ordering/CheckoutContext'
import { useCustomer } from 'public/components/online_ordering/CustomerContextCommon'
import {
  PaymentOption,
  usePayment
} from 'public/components/online_ordering/PaymentContext'
import { useFulfillment } from 'src/public/components/online_ordering/FulfillmentContext'
import { DiningOptionBehavior } from 'src/apollo/onlineOrdering'

import CheckoutSection from './CheckoutSection'
import OrderSection from './OrderSection'
import PaymentSection, { TipAndPricesSection } from './PaymentSection'
import TipSection from './fulfillment/TipSection'
import { getEcommShippingFee } from 'public/components/default_template/online_ordering/cart/cartUtils'
import { Channel } from 'public/js/siteUtilities'
import FulfillmentTimeModal from 'public/components/default_template/online_ordering/dining_behavior_toggle/FulfillmentTimeModal'

const CheckoutSections = () => {
  const { customer } = useCustomer()
  const { cart, channel } = useCart()
  const { orderTotal, orderError } = useCheckout()
  const { paymentOption } = usePayment()
  const isIntlRestaurant = useIsIntlRestaurant()
  const isMobile = useIsMobile(ScreenWidth.SMALL)
  const [showIntlPayment, setShowIntlPayment] = useState<boolean>(false)
  const canPayAtCheckout = !(
    cart?.paymentOptions.atCheckout.length === 0 &&
    cart?.paymentOptions.uponReceipt.length > 0
  )
  const showUSPayments = !showIntlPayment
  const { globalNavHeight, globalNavOpen } = useGlobalNav()
  const { selectedDiningOptionBehavior } = useFulfillment()
  const isDelivery =
    selectedDiningOptionBehavior === DiningOptionBehavior.Delivery

  useEffect(() => {
    if (isIntlRestaurant && orderError) {
      setShowIntlPayment(false)
    }
  }, [isIntlRestaurant, orderError])

  return (
    <div className='checkoutFormContainer'>
      <FulfillmentTimeModal />
      <div className='checkoutFormContents fulfillmentInformation' role='form'>
        {isMobile && (
          <>
            {/* TODO grey background + full width + new styling */}
            <OrderSection />
            <LocationSection />
          </>
        )}

        <FulfillmentSectionV2 />

        {!customer && (
          <>
            <div className='checkoutModeButtonGroup'>
              <ToastCheckoutButton />
            </div>
            <div className='separatorWithText'>or</div>
          </>
        )}

        <CustomerInfoSection />

        <div className='checkoutFormContents' data-testid='checkoutFormInfo'>
          <PaymentSection />
        </div>
        {/* TODO disable pay now/pay at restaurant radio buttons when this pops up */}
        {isIntlRestaurant && (
          <InternationalPaymentContainer
            expanded={showIntlPayment}
            hideIntlPayment={() => setShowIntlPayment(false)}
          />
        )}
      </div>
      <div
        className='checkoutFormContents checkoutOrderSummary'
        role='form'
        style={globalNavOpen ? { top: `${globalNavHeight}px` } : undefined}
      >
        {!isMobile && <LocationSection />}
        {/* wrapper div to prevent a gap between the two bordered checkout sections */}
        <div>
          <OrderSection />
          <CheckoutSection
            title={'Discounts & Gift Cards'}
            collapsible
            icon={<TagIcon />}
          >
            {showUSPayments && <DiscountsSection />}
          </CheckoutSection>
          {isDelivery && <TipSection />}
        </div>
        <TipAndPricesSection isExpanded={showUSPayments} />
        <CheckoutSection>
          <div className='sectionButton'>
            {isIntlRestaurant &&
            orderTotal > 0 &&
            canPayAtCheckout &&
            paymentOption === PaymentOption.PayNow ? (
              <ContinuePaymentButton
                enableIntlPayment={() => setShowIntlPayment(true)}
              />
            ) : (
              <SubmitOrderButton
                disabled={Boolean(
                  channel === Channel.ECOMM &&
                    cart?.diningOptionBehavior ===
                      DiningOptionBehavior.TakeOut &&
                    !getEcommShippingFee(cart) &&
                    cart?.order?.deliveryInfo
                )}
              />
            )}
            <CheckoutNote
              showIntlPayment={showIntlPayment}
              paymentOption={paymentOption}
            />
          </div>
        </CheckoutSection>
        <UberCheckoutNote />
      </div>
    </div>
  )
}

export default CheckoutSections
